import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Flame, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import HeroMarquee from "../components/HeroMarquee";
import PlayerOfTheGame from "../components/PlayerOfTheGame";
import GameAlertSignup from "../components/GameAlertSignup";
import SponsorSpotlight from "../components/SponsorSpotlight";
import FeaturedProducts from "../components/FeaturedProducts";
import StructuredData from "../components/StructuredData";
import { useSettings } from "../contexts/SettingsContext";

export default function Home() {
    const [players, setPlayers] = useState([]);
    const [games, setGames] = useState([]);
    const [news, setNews] = useState([]);
    const { settings } = useSettings();

    useEffect(() => {
        api.get("/players").then((r) => setPlayers(r.data.slice(0, 4)));
        api.get("/games").then((r) => setGames(r.data.slice(0, 4)));
        api.get("/news").then((r) => setNews(r.data.slice(0, 3)));
    }, []);

    const heroImage = settings?.hero_image_url || "https://images.pexels.com/photos/15048601/pexels-photo-15048601.jpeg";
    const heroHeadline = settings?.hero_headline || "Purple Kings";
    const heroSubline = settings?.hero_subline || "Level Up.";
    const heroBlurb = settings?.hero_blurb || "Built in Texas. Crowned in gold. ABA professional basketball since 2004.";
    const logoUrl = settings?.logo_url;

    return (
        <div data-testid="home-page" className="grain">
            <StructuredData data={{
                "@context": "https://schema.org",
                "@type": "SportsTeam",
                "name": "Purple Kings",
                "alternateName": "Texas Purple Kings",
                "sport": "Basketball",
                "url": "https://purplekingshoops.com/",
                "logo": settings?.logo_url ? `https://purplekingshoops.com${settings.logo_url}` : undefined,
                "image": settings?.hero_image_url ? `https://purplekingshoops.com${settings.hero_image_url}` : undefined,
                "description": heroBlurb,
                "memberOf": { "@type": "SportsOrganization", "name": "ABA (American Basketball Association)" },
                "location": {
                    "@type": "Place",
                    "name": settings?.contact_address || "Stellar Bank Gym at Fulshear High School",
                    "address": { "@type": "PostalAddress", "addressLocality": "Fulshear", "addressRegion": "TX", "addressCountry": "US" },
                },
                "email": settings?.contact_email,
                "telephone": settings?.contact_phone,
                "award": "2024–2025 ABA South Central Blue Division Wild Card Champions",
                "sameAs": ["https://purplekingshoops.com/"],
            }} />
            {/* HERO */}
            <section className="relative min-h-[88vh] md:min-h-[760px] overflow-hidden hero-clip py-24 md:py-0">
                <div
                    className="absolute inset-0 bg-cover bg-center scale-105"
                    style={{
                        backgroundImage: `url('${heroImage}')`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />

                <div className="absolute inset-x-0 top-1/4 opacity-40">
                    <HeroMarquee />
                </div>

                <div className="relative max-w-7xl mx-auto px-5 md:px-10 md:min-h-[760px] flex items-center">
                    <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center w-full">
                        {/* LEFT: Big logo + championship plaque */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="md:col-span-5 flex flex-col items-center md:items-start"
                            data-testid="hero-logo-block"
                        >
                            {logoUrl && (
                                <img
                                    src={logoUrl}
                                    alt="Purple Kings"
                                    data-testid="hero-big-logo"
                                    className="w-48 sm:w-56 md:w-full md:max-w-[320px] lg:max-w-[360px] drop-shadow-[0_8px_40px_rgba(155,93,229,0.45)]"
                                />
                            )}
                            <div
                                data-testid="hero-championship-banner"
                                className="mt-6 md:mt-8 w-full max-w-[460px] border-y border-gold py-4 px-3 text-center"
                            >
                                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-black">
                                    2024 – 2025
                                </div>
                                <div className="text-white text-sm md:text-base mt-1 font-bold tracking-wide leading-snug">
                                    ABA South Central Blue Division
                                </div>
                                <div className="h-display text-2xl md:text-3xl text-gold mt-1 leading-none">
                                    Wild Card Champions
                                </div>
                            </div>
                        </motion.div>

                        {/* RIGHT: Headline + blurb + CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                            className="md:col-span-7"
                        >
                            <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-6 flex items-center gap-3">
                                <span className="h-px w-12 bg-gold" />
                                ABA Professional Basketball · Texas
                            </div>
                            <h1 data-testid="hero-title" className="h-display text-5xl sm:text-6xl md:text-7xl text-white leading-[0.85] mb-2">
                                {heroHeadline.split(" ").map((word, i) => (
                                    <span key={i} className={word.toLowerCase() === "purple" ? "text-gold" : ""}>{word}{i < heroHeadline.split(" ").length - 1 ? " " : ""}</span>
                                ))}
                            </h1>
                            <div className="h-display text-4xl sm:text-5xl md:text-6xl text-purple-light leading-[0.85] mb-7">{heroSubline}</div>
                            <p className="text-white/80 text-lg max-w-xl leading-relaxed mb-8 font-medium">
                                {heroBlurb}
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/schedule" data-testid="hero-cta-schedule" className="btn-gold px-7 py-4 text-sm inline-flex items-center gap-2">
                                    See Upcoming Games <ArrowRight size={16} />
                                </Link>
                                <Link to="/tryouts" data-testid="hero-cta-tryouts" className="btn-ghost-gold px-7 py-4 text-sm">
                                    Open Tryouts
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>

                <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 text-ink text-[10px] uppercase tracking-[0.4em]">
                    Scroll
                </div>
            </section>

            {/* STAT STRIP */}
            <section className="border-y border-line bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-line">
                    {[
                        { label: "Wins Since 2024", value: "45" },
                        { label: "Avg PPG", value: "120.1" },
                        { label: "Playoff Wins", value: "7" },
                        { label: "Years In ABA", value: "3" },
                    ].map((s, i) => (
                        <div key={i} className="p-6 md:p-10" data-testid={`stat-${i}`}>
                            <div className="h-display text-5xl md:text-6xl text-gold leading-none">{s.value}</div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-ink mt-3 font-bold">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-4">01 · The Kingdom</div>
                    <h2 className="h-display text-5xl md:text-6xl text-white">Built In Texas.<br />Crowned in Gold.</h2>
                </div>
                <div className="md:col-span-7 space-y-6">
                    <p className="text-white/80 text-lg leading-relaxed">
                        The Purple Kings represent more than basketball — we represent the rise of every Texan who refused to settle. Pro-grade hoops since 2004, neighborhood roots, championship hunger.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                        {[
                            { icon: Crown, t: "Royalty", d: "Carry yourself like a king on and off the floor." },
                            { icon: Flame, t: "Grit", d: "Texas tough. Outwork everyone." },
                            { icon: Trophy, t: "Glory", d: "Compete for hardware. Always." },
                        ].map((v, i) => (
                            <div key={i} className="card-flat p-5">
                                <v.icon className="text-gold" size={22} />
                                <div className="h-display text-xl mt-4">{v.t}</div>
                                <p className="text-ink text-sm mt-2">{v.d}</p>
                            </div>
                        ))}
                    </div>
                    <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase font-bold hover:text-gold-bright">
                        Our Mission <ArrowRight size={14} />
                    </Link>
                </div>
            </section>

            {/* PLAYERS PREVIEW */}
            <section className="bg-bg-soft border-y border-line py-24">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3">02 · The Kings</div>
                            <h2 className="h-display text-5xl md:text-6xl">Meet The Roster</h2>
                        </div>
                        <Link to="/roster" data-testid="home-roster-link" className="text-gold text-xs uppercase tracking-[0.3em] font-bold hover:text-gold-bright">
                            Full Roster →
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {players.map((p, i) => (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                className="card-flat overflow-hidden group"
                                data-testid={`home-player-${i}`}
                            >
                                <div
                                    className="aspect-[3/4] bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                    style={{ backgroundImage: `url('${p.photo_url || ""}')` }}
                                />
                                <div className="p-4">
                                    <div className="text-gold text-xs tracking-widest font-bold">#{p.jersey_number} · {p.position}</div>
                                    <div className="h-display text-lg mt-1 leading-tight">{p.name}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SCHEDULE PREVIEW */}
            <section className="max-w-7xl mx-auto px-5 md:px-10 py-24">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3">03 · The Court</div>
                        <h2 className="h-display text-5xl md:text-6xl">Next Up</h2>
                    </div>
                    <Link to="/schedule" data-testid="home-schedule-link" className="text-gold text-xs uppercase tracking-[0.3em] font-bold hover:text-gold-bright">
                        All Games →
                    </Link>
                </div>
                <div className="space-y-3">
                    {games.map((g, i) => (
                        <div key={g.id} className="card-flat p-5 md:p-6 flex flex-wrap items-center gap-4" data-testid={`home-game-${i}`}>
                            <div className="w-24">
                                <div className="h-display text-3xl text-gold leading-none">
                                    {new Date(g.date).toLocaleDateString("en-US", { day: "2-digit", timeZone: "America/Chicago" })}
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink mt-1">
                                    {new Date(g.date).toLocaleDateString("en-US", { month: "short", timeZone: "America/Chicago" })}
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-ink">
                                    {g.home_away === "home" ? "Home" : "Away"} · {g.location}
                                </div>
                                <div className="h-display text-xl mt-1">
                                    Purple Kings vs. {g.opponent}
                                </div>
                            </div>
                            <div className="ml-auto">
                                {g.status === "final" ? (
                                    <div className={`h-display text-xl ${g.result?.startsWith("W") ? "text-green-400" : "text-red-400"}`}>{g.result}</div>
                                ) : (
                                    <div className="h-display text-sm text-gold">
                                        {new Date(g.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })} <span className="text-ink text-[10px]">CT</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PLAYER OF THE GAME */}
            <PlayerOfTheGame />

            {/* FEATURED PRODUCTS */}
            <FeaturedProducts />

            {/* GAME ALERTS SIGNUP */}
            <GameAlertSignup source="home" />

            {/* SPONSOR SPOTLIGHT */}
            <SponsorSpotlight />

            {/* NEWS PREVIEW */}
            <section className="bg-bg-soft border-y border-line py-24">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3">04 · The Word</div>
                            <h2 className="h-display text-5xl md:text-6xl">Latest News</h2>
                        </div>
                        <Link to="/news" data-testid="home-news-link" className="text-gold text-xs uppercase tracking-[0.3em] font-bold hover:text-gold-bright">All News →</Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        {news.map((n, i) => (
                            <Link key={n.id} to={`/news/${n.slug}`} className="card-flat group overflow-hidden" data-testid={`home-news-${i}`}>
                                <div
                                    className="aspect-video bg-cover bg-center transition-transform group-hover:scale-105"
                                    style={{ backgroundImage: `url('${n.cover_image || ""}')` }}
                                />
                                <div className="p-5">
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">
                                        {new Date(n.published_at || n.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
                                    </div>
                                    <div className="h-display text-xl leading-tight group-hover:text-gold transition-colors">{n.title}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-24">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-dark to-bg" />
                <div className="relative max-w-5xl mx-auto px-5 md:px-10 text-center">
                    <Crown className="text-gold mx-auto" size={36} />
                    <h2 className="h-display text-5xl md:text-7xl mt-4">Wear The Crown.</h2>
                    <p className="text-white/80 text-lg mt-4 max-w-2xl mx-auto">
                        Open tryouts are live. Step on the floor. Earn your spot in the kingdom.
                    </p>
                    <Link to="/tryouts" data-testid="home-cta-tryouts" className="btn-gold px-8 py-4 inline-block mt-7 text-sm">
                        Register For Tryouts
                    </Link>
                </div>
            </section>
        </div>
    );
}
